import { useState } from "react";
import { ChevronDown } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RecommendedBadge } from "@/components/ui/RecommendedBadge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useRole } from "@/hooks/auth/useRole";
import { useLlmModels } from "@/hooks/voice/useLlmModels";
import { PronunciationPair } from "@/types/agent";
import { SetupAnalysisResult } from "@/types/agent/setup";
import { ReviewSectionCard } from "./ReviewSectionCard";

interface ConfirmationAdvancedSectionProps {
  analysis: SetupAnalysisResult;
  pronunciations: PronunciationPair[];
  onChange: (field: keyof SetupAnalysisResult, value: any) => void;
  onConversationSettingsChange: (field: string, value: number | string) => void;
  onSupportEmailsChange: (emails: string[]) => void;
  onPronunciationsChange: (pronunciations: PronunciationPair[]) => void;
}

export const ConfirmationAdvancedSection = ({
  analysis,
  pronunciations,
  onChange,
  onConversationSettingsChange,
  onSupportEmailsChange,
  onPronunciationsChange,
}: ConfirmationAdvancedSectionProps) => {
  const { data: llmModels = [] } = useLlmModels();
  const { isInternalAdmin } = useRole();
  const [allSectionsOpen, setAllSectionsOpen] = useState(false);
  const [sectionsState, setSectionsState] = useState({
    systemPrompt: false,
    pronunciations: false,
    conversationSettings: false,
    businessContext: false,
    technicalSettings: false,
    voiceSettings: false,
    dynamicVariables: false,
    goal: false,
  });

  const toggleAllSections = () => {
    const newState = !allSectionsOpen;
    setAllSectionsOpen(newState);
    setSectionsState({
      systemPrompt: newState,
      pronunciations: newState,
      conversationSettings: newState,
      businessContext: newState,
      technicalSettings: newState,
      voiceSettings: newState,
      dynamicVariables: newState,
      goal: newState,
    });
  };

  const updateSectionState = (section: keyof typeof sectionsState, isOpen: boolean) => {
    setSectionsState((prev) => ({ ...prev, [section]: isOpen }));
    const updatedState = { ...sectionsState, [section]: isOpen };
    setAllSectionsOpen(Object.values(updatedState).every((state) => state));
  };

  const addPronunciation = () => {
    onPronunciationsChange([...pronunciations, { word: "", pronunciation: "" }]);
  };

  const removePronunciation = (index: number) => {
    onPronunciationsChange(pronunciations.filter((_, i) => i !== index));
  };

  const updatePronunciation = (index: number, field: "word" | "pronunciation", value: string) => {
    const updated = [...pronunciations];
    updated[index][field] = value;
    onPronunciationsChange(updated);
  };

  return (
    <ReviewSectionCard
      title="Advanced Settings"
      subtitle="Optional fine-tuning parameters"
      genieIcon="palette"
      brand="#6B7280"
    >
      <div className="flex justify-end mb-4">
        <Button
          variant="outline"
          size="sm"
          onClick={toggleAllSections}
          className="flex items-center gap-1 border-gray-300 hover:bg-gray-50"
        >
          {allSectionsOpen ? "Collapse All" : "Expand All"}
        </Button>
      </div>

      <div className="space-y-4">
        {/* System Prompt - Enhanced with informative messaging */}
        <Collapsible
          open={sectionsState.systemPrompt}
          onOpenChange={(isOpen) => updateSectionState("systemPrompt", isOpen)}
          className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
        >
          <CollapsibleTrigger className="flex items-start justify-between w-full p-5 hover:bg-gray-50 transition-colors gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm sm:text-base font-semibold text-gray-900">System Prompt</span>
                <Badge
                  variant="outline"
                  className="text-xs border-[#4E9CFF]/30 text-[#4E9CFF] bg-[#4E9CFF]/10 shrink-0"
                >
                  Advanced
                </Badge>
              </div>
              <p className="text-xs text-gray-500 mt-1">Core instructions that guide your genie's conversations</p>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${sectionsState.systemPrompt ? "rotate-180" : ""}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50">
            <div>
              <div className="flex flex-col md:flex-row md:items-start md:justify-between md:gap-3 mb-2">
                <Label htmlFor="systemPrompt" className="text-xs sm:text-sm font-medium">
                  Instructions & Behavior
                </Label>
                <p className="text-xs text-[#4E9CFF] font-semibold text-left md:text-right md:flex-1 mt-2 md:mt-0">
                  We'll create your initial prompt using our recommended structure based on best practices. You can
                  easily customize it anytime.
                </p>
              </div>
              <Textarea
                id="systemPrompt"
                value={analysis.systemPrompt}
                onChange={(e) => onChange("systemPrompt", e.target.value)}
                className="mt-2 text-sm p-3"
                rows={10}
                placeholder="You are a helpful assistant that..."
              />
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Pronunciations - Moved to position 2 */}
        <Collapsible
          open={sectionsState.pronunciations}
          onOpenChange={(isOpen) => updateSectionState("pronunciations", isOpen)}
          className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
        >
          <CollapsibleTrigger className="flex items-start justify-between w-full p-5 hover:bg-gray-50 transition-colors gap-3">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <span className="text-sm sm:text-base font-semibold text-gray-900">Pronunciations</span>
              <span className="text-xs sm:text-sm text-gray-500">({pronunciations.length})</span>
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${sectionsState.pronunciations ? "rotate-180" : ""}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50 space-y-4">
            <div>
              <Label className="text-sm">Custom Pronunciations</Label>
              <p className="text-xs text-gray-500 mt-1">
                Add custom pronunciations for words or names that should be pronounced differently.
              </p>
            </div>

            <div className="space-y-3">
              {pronunciations.map((pronunciation, index) => (
                <div key={index} className="flex flex-col sm:flex-row gap-2 items-end">
                  <div className="flex-1">
                    <Label className="text-xs">Word</Label>
                    <Input
                      value={pronunciation.word}
                      onChange={(e) => updatePronunciation(index, "word", e.target.value)}
                      placeholder="e.g., 'LinkedIn'"
                      className="mt-1 text-sm h-9"
                    />
                  </div>
                  <div className="flex-1">
                    <Label className="text-xs">Pronunciation</Label>
                    <Input
                      value={pronunciation.pronunciation}
                      onChange={(e) => updatePronunciation(index, "pronunciation", e.target.value)}
                      placeholder="e.g., 'Linked In'"
                      className="mt-1 text-sm h-9"
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => removePronunciation(index)}
                    className="px-3 py-2 text-[#FF4DB8] hover:bg-[#FF4DB8]/10 border-[#FF4DB8]/30"
                  >
                    Remove
                  </Button>
                </div>
              ))}

              {pronunciations.length === 0 && (
                <p className="text-sm text-gray-500 text-center py-4">
                  No custom pronunciations added yet. Click "Add Pronunciation" to get started.
                </p>
              )}

              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addPronunciation}
                className="w-full text-[#4E9CFF] hover:bg-[#4E9CFF]/10 border-[#4E9CFF]/30 border-dashed"
              >
                + Add Pronunciation
              </Button>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Conversation Settings */}
        <Collapsible
          open={sectionsState.conversationSettings}
          onOpenChange={(isOpen) => updateSectionState("conversationSettings", isOpen)}
          className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
        >
          <CollapsibleTrigger className="flex items-start justify-between w-full p-5 hover:bg-gray-50 transition-colors gap-3">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <span className="text-sm sm:text-base font-semibold text-gray-900">Conversation Settings</span>
              <RecommendedBadge />
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${sectionsState.conversationSettings ? "rotate-180" : ""}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <Label htmlFor="maxDuration" className="text-xs sm:text-sm">
                  Max Duration (sec)
                </Label>
                <Input
                  id="maxDuration"
                  type="number"
                  value={analysis.conversationSettings.maxDuration}
                  onChange={(e) => onConversationSettingsChange("maxDuration", parseInt(e.target.value))}
                  className="mt-1 text-sm h-9"
                />
              </div>
              <div>
                <Label htmlFor="turnTimeout" className="text-xs sm:text-sm">
                  Turn Timeout (sec)
                </Label>
                <Input
                  id="turnTimeout"
                  type="number"
                  value={analysis.conversationSettings.turnTimeout}
                  onChange={(e) => onConversationSettingsChange("turnTimeout", parseInt(e.target.value))}
                  className="mt-1 text-sm h-9"
                />
              </div>
              <div>
                <Label htmlFor="silenceTimeout" className="text-xs sm:text-sm">
                  Silence Timeout (sec)
                </Label>
                <Input
                  id="silenceTimeout"
                  type="number"
                  value={analysis.conversationSettings.silenceTimeout}
                  onChange={(e) => onConversationSettingsChange("silenceTimeout", parseInt(e.target.value))}
                  className="mt-1 text-sm h-9"
                />
              </div>
              <div className="sm:col-span-2">
                <Label htmlFor="turnMode" className="text-xs sm:text-sm">
                  Turn Mode
                </Label>
                <Select
                  value={analysis.conversationSettings.turnMode || "silence"}
                  onValueChange={(value) => onConversationSettingsChange("turnMode", value)}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="silence">Silence</SelectItem>
                    <SelectItem value="turn">Turn</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CollapsibleContent>
        </Collapsible>

        {/* Business Context */}
        <Collapsible
          open={sectionsState.businessContext}
          onOpenChange={(isOpen) => updateSectionState("businessContext", isOpen)}
          className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
        >
          <CollapsibleTrigger className="flex items-start justify-between w-full p-5 hover:bg-gray-50 transition-colors gap-3">
            <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
              <span className="text-sm sm:text-base font-semibold text-gray-900">Business Context</span>
              <RecommendedBadge />
            </div>
            <ChevronDown
              className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${sectionsState.businessContext ? "rotate-180" : ""}`}
            />
          </CollapsibleTrigger>
          <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50 space-y-4">
            {analysis.brand && (
              <div>
                <Label htmlFor="brand">Brand</Label>
                <Input
                  id="brand"
                  value={analysis.brand}
                  onChange={(e) => onChange("brand", e.target.value)}
                  className="mt-1 text-sm h-9"
                />
              </div>
            )}

            {analysis.description && (
              <div>
                <Label htmlFor="description">Description</Label>
                <Textarea
                  id="description"
                  value={analysis.description}
                  onChange={(e) => onChange("description", e.target.value)}
                  className="mt-1 text-sm p-2"
                  rows={2}
                />
              </div>
            )}
          </CollapsibleContent>
        </Collapsible>

        {/* Technical Settings */}
        {(analysis.llmModel ||
          analysis.temperature !== undefined ||
          analysis.maxTokens ||
          analysis.textOnly !== undefined) && (
          <Collapsible
            open={sectionsState.technicalSettings}
            onOpenChange={(isOpen) => updateSectionState("technicalSettings", isOpen)}
            className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            <CollapsibleTrigger className="flex items-start justify-between w-full p-5 hover:bg-gray-50 transition-colors gap-3">
              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                <span className="text-sm sm:text-base font-semibold text-gray-900">AI & Technical Settings</span>
                <RecommendedBadge />
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${sectionsState.technicalSettings ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.llmModel && (
                  <div>
                    <Label htmlFor="llmModel">AI Model</Label>
                    <Select value={analysis.llmModel} onValueChange={(value) => onChange("llmModel", value)}>
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {llmModels.map((model) => (
                          <SelectItem key={model.id} value={model.id}>
                            {model.name} ({model.provider})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {analysis.temperature !== undefined && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="temperature">Temperature</Label>
                      <span className="text-sm font-semibold text-[#4E9CFF]">{analysis.temperature.toFixed(2)}</span>
                    </div>
                    <Input
                      id="temperature"
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={analysis.temperature}
                      onChange={(e) => onChange("temperature", parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {analysis.maxTokens && (
                  <div>
                    <Label htmlFor="maxTokens">Max Tokens</Label>
                    <Input
                      id="maxTokens"
                      type="number"
                      value={analysis.maxTokens}
                      onChange={(e) => onChange("maxTokens", parseInt(e.target.value))}
                      className="mt-1 text-sm h-9"
                    />
                  </div>
                )}

                {analysis.textOnly !== undefined && (
                  <div className="flex items-center space-x-2">
                    <Label htmlFor="textOnly">Text Only Mode</Label>
                    <Switch
                      id="textOnly"
                      checked={analysis.textOnly}
                      onCheckedChange={(checked) => onChange("textOnly", checked)}
                    />
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Voice Settings */}
        {(analysis.voiceStability !== undefined ||
          analysis.voiceSimilarity !== undefined ||
          analysis.speechSpeed !== undefined) && (
          <Collapsible
            open={sectionsState.voiceSettings}
            onOpenChange={(isOpen) => updateSectionState("voiceSettings", isOpen)}
            className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            <CollapsibleTrigger className="flex items-start justify-between w-full p-5 hover:bg-gray-50 transition-colors gap-3">
              <div className="flex items-center gap-2 flex-wrap flex-1 min-w-0">
                <span className="text-sm sm:text-base font-semibold text-gray-900">Voice Settings</span>
                <RecommendedBadge />
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ${sectionsState.voiceSettings ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50 space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                {analysis.voiceStability !== undefined && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="voiceStability">Stability</Label>
                      <span className="text-sm font-semibold text-[#4E9CFF]">{analysis.voiceStability.toFixed(2)}</span>
                    </div>
                    <Input
                      id="voiceStability"
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={analysis.voiceStability}
                      onChange={(e) => onChange("voiceStability", parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                )}

                {analysis.voiceSimilarity !== undefined && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="voiceSimilarity">Similarity</Label>
                      <span className="text-sm font-semibold text-[#4E9CFF]">
                        {analysis.voiceSimilarity.toFixed(2)}
                      </span>
                    </div>
                    <Input
                      id="voiceSimilarity"
                      type="range"
                      min="0"
                      max="1"
                      step="0.1"
                      value={analysis.voiceSimilarity}
                      onChange={(e) => onChange("voiceSimilarity", parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                )}

                {analysis.speechSpeed !== undefined && (
                  <div>
                    <div className="flex items-center justify-between mb-1">
                      <Label htmlFor="speechSpeed">Speed</Label>
                      <span className="text-sm font-semibold text-[#4E9CFF]">{analysis.speechSpeed.toFixed(2)}</span>
                    </div>
                    <Input
                      id="speechSpeed"
                      type="range"
                      min="0.7"
                      max="1.2"
                      step="0.1"
                      value={analysis.speechSpeed}
                      onChange={(e) => onChange("speechSpeed", parseFloat(e.target.value))}
                      className="mt-1"
                    />
                  </div>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Dynamic Variables */}
        {analysis.dynamicVariables && Object.keys(analysis.dynamicVariables).length > 0 && (
          <Collapsible
            open={sectionsState.dynamicVariables}
            onOpenChange={(isOpen) => updateSectionState("dynamicVariables", isOpen)}
            className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm hover:shadow-md transition-shadow"
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-gray-50 transition-colors">
              <span className="text-base font-semibold text-gray-900">Dynamic Variables</span>
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform ${sectionsState.dynamicVariables ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-5 pb-5 pt-1 bg-gray-50/50">
              <div>
                <Label>Dynamic Variables</Label>
                <div className="mt-1 space-y-2">
                  {Object.entries(analysis.dynamicVariables).map(([key, value]) => (
                    <div key={key} className="flex items-center gap-2 p-2 bg-gray-50 rounded-md">
                      <Badge variant="outline">{key}</Badge>
                      <span className="text-sm text-gray-600">{value}</span>
                    </div>
                  ))}
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}

        {/* Admin-Only: Genie Flags */}
        {isInternalAdmin() && (
          <Collapsible
            open={sectionsState.goal}
            onOpenChange={(isOpen) => updateSectionState("goal", isOpen)}
            className="border border-yellow-200 rounded-xl overflow-hidden bg-yellow-50/30 shadow-sm hover:shadow-md transition-shadow"
          >
            <CollapsibleTrigger className="flex items-center justify-between w-full p-5 hover:bg-yellow-50/50 transition-colors">
              <div className="flex items-center gap-2">
                <span className="text-base font-semibold text-gray-900">Admin Settings</span>
                <Badge className="bg-yellow-600 text-white text-xs">Admin Only</Badge>
              </div>
              <ChevronDown
                className={`w-5 h-5 text-gray-500 transition-transform ${sectionsState.goal ? "rotate-180" : ""}`}
              />
            </CollapsibleTrigger>
            <CollapsibleContent className="px-5 pb-5 pt-1 bg-white space-y-4">
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-gray-900">Marketplace Genie</Label>
                    <p className="text-xs text-gray-500 mt-1">List this genie in the marketplace</p>
                  </div>
                  <Switch
                    checked={analysis.isMarketplaceGenie || false}
                    onCheckedChange={(checked) => onChange("isMarketplaceGenie", checked)}
                    className="data-[state=checked]:bg-[#FF4DB8]"
                  />
                </div>

                <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                  <div>
                    <Label className="text-sm font-medium text-gray-900">Demo Genie</Label>
                    <p className="text-xs text-gray-500 mt-1">Mark this as a demo or test genie</p>
                  </div>
                  <Switch
                    checked={analysis.isDemoGenie || false}
                    onCheckedChange={(checked) => onChange("isDemoGenie", checked)}
                    className="data-[state=checked]:bg-[#4E9CFF]"
                  />
                </div>
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      </div>
    </ReviewSectionCard>
  );
};
